import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  InventoryVolumeCreateSchema,
  InventoryVolumeQueryAllSchema,
  InventoryVolumeQuerySchema,
  InventoryVolumeUpdateSchema,
} from './schemas';

export const useGetInventoryInventoryVolumesMutation: UseControlledMutation<InventoryVolumeQuerySchema> = () =>
  useMutation({
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['INVENTORY_INVENTORY_VOLUMES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InventoryVolumeQueryAllSchema>({
            endpoint: '/api/v1/inventory-volumes',
            params: { search, per_page },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetInventoryInventoryVolumes(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['INVENTORY_VOLUMES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<InventoryVolumeQueryAllSchema>({
        endpoint: '/api/v1/inventory-volumes?include=inventories',
        params: `${apiParamStr}`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['INVENTORY_VOLUMES'] });
};

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, InventoryVolumeCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/inventory-volumes/',
        payload,
      }),
  });

export function useGetInventoryVolume(id: string) {
  return useSuspenseQuery({
    queryKey: ['INVENTORY_VOLUME', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: InventoryVolumeQuerySchema }>({
        endpoint: `/api/v1/inventory-volumes/${id}`,
        params: { include: `inventories` },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: InventoryVolumeQuerySchema }, ApiError, Partial<InventoryVolumeUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/inventory-volumes/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['INVENTORY_VOLUME_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/inventory-volumes/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
